import React, { useMemo } from 'react';
import { motion } from 'motion/react';
import { MoonIcon, SunIcon } from '@heroicons/react/16/solid';
import clsx from 'clsx';

const APPEARANCE = {
  default: {
    track: 'bg-gray-200/90 border border-gray-300/80 hover:border-gray-400/80',
    thumb: 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.12)]',
    iconOnThumb: 'text-amber-500',
    iconOnTrack: 'text-gray-400',
  },
  inverse: {
    track: 'bg-white/10 border border-white/20 backdrop-blur-md hover:border-white/30',
    thumb: 'bg-white shadow-[0_2px_10px_rgba(0,0,0,0.35)]',
    iconOnThumb: 'text-amber-600',
    iconOnTrack: 'text-white/35',
  },
  authLight: {
    track: 'bg-[#e8e8ed] border border-gray-300/90 hover:border-gray-400',
    thumb: 'bg-white shadow-[0_1px_3px_rgba(0,0,0,0.1)]',
    iconOnThumb: 'text-amber-500',
    iconOnTrack: 'text-gray-400',
  },
  authDark: {
    track: 'bg-gray-800 border border-gray-600 hover:border-gray-500',
    thumb: 'bg-white shadow-[0_2px_8px_rgba(0,0,0,0.45)]',
    iconOnThumb: 'text-gray-900',
    iconOnTrack: 'text-gray-500',
  },
};

/** Ancho/alto exterior (border-box, incluye borde 1px). */
const SIZES = {
  sm: { width: 60, height: 34, thumb: 28, icon: 15 },
  md: { width: 68, height: 38, thumb: 32, icon: 17 },
};

const BORDER_PX = 1;

function getThumbGeometry(cfg) {
  const innerW = cfg.width - BORDER_PX * 2;
  const innerH = cfg.height - BORDER_PX * 2;
  /** Centra el thumb en la mitad izquierda / derecha del track interior. */
  const leftLight = innerW / 4 - cfg.thumb / 2;
  const leftDark = (3 * innerW) / 4 - cfg.thumb / 2;
  const travel = leftDark - leftLight;
  const top = (innerH - cfg.thumb) / 2;
  return { leftLight, travel, top, innerW, innerH };
}

/**
 * @param {'default' | 'inverse' | 'authLight' | 'authDark'} appearance
 * @param {'sm' | 'md'} size
 */
export default function ThemeToggle({
  modoOscuro,
  onToggle,
  appearance = 'default',
  size = 'md',
  className,
}) {
  const palette = APPEARANCE[appearance] || APPEARANCE.default;
  const cfg = SIZES[size] || SIZES.md;
  const isDark = Boolean(modoOscuro);
  const geom = useMemo(() => getThumbGeometry(cfg), [cfg]);

  const ActiveIcon = isDark ? MoonIcon : SunIcon;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro'}
      onClick={() => onToggle(!isDark)}
      className={clsx(
        'relative box-border inline-flex shrink-0 cursor-pointer overflow-hidden rounded-full p-0 transition-colors duration-200',
        'touch-manipulation select-none',
        palette.track,
        className
      )}
      style={{
        width: cfg.width,
        height: cfg.height,
        minWidth: cfg.width,
        minHeight: cfg.height,
      }}
    >
      <span className="pointer-events-none absolute inset-0 flex" aria-hidden>
        <span className="flex flex-1 items-center justify-center">
          {isDark && (
            <SunIcon
              className={clsx('shrink-0', palette.iconOnTrack)}
              style={{ width: cfg.icon, height: cfg.icon }}
            />
          )}
        </span>
        <span className="flex flex-1 items-center justify-center">
          {!isDark && (
            <MoonIcon
              className={clsx('shrink-0', palette.iconOnTrack)}
              style={{ width: cfg.icon, height: cfg.icon }}
            />
          )}
        </span>
      </span>

      <motion.span
        aria-hidden
        className={clsx(
          'absolute z-10 box-border flex items-center justify-center',
          palette.thumb
        )}
        style={{
          width: cfg.thumb,
          height: cfg.thumb,
          borderRadius: '50%',
          top: geom.top,
          left: geom.leftLight,
        }}
        initial={false}
        animate={{ x: isDark ? geom.travel : 0 }}
        transition={{ type: 'spring', stiffness: 480, damping: 34, mass: 0.65 }}
      >
        <ActiveIcon
          className={clsx('shrink-0', palette.iconOnThumb)}
          style={{ width: cfg.icon, height: cfg.icon }}
        />
      </motion.span>
    </button>
  );
}
